import { LLMFeedbackResponse } from '@ltvco/refresh-lib/api';
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useLLMFeedback } from '@ltvco/refresh-lib/hooks';
import { useContext, useEffect, useState } from 'react';

import { CHATBV_GA4_EVENTS } from 'components/ChatBV/constants';
import { Message } from 'components/ChatBV/types';

export const useFeedback = (messages: Message[]) => {
  const [openFeedback, setOpenFeedback] = useState(false);
  const [thumbsUp, setThumbsUp] = useState(false);
  const [currentResponseId, setCurrentResponseId] = useState<string>('');
  const [responseIds, setResponseIds] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<Record<string, LLMFeedbackResponse>>(
    {}
  );
  const { trackEventGA4 } = useContext(AppConfig);
  const { feedbackQueries, createFeedback, updateFeedback } =
    useLLMFeedback(responseIds);

  // Get response_ids for each message
  useEffect(() => {
    setResponseIds(
      messages.map((m) => m.response_id || '').filter((id) => id !== '')
    );
  }, [messages]);

  useEffect(() => {
    if (feedbackQueries.length > 0) {
      feedbackQueries.forEach((q) => {
        if (q.data && (q.data as LLMFeedbackResponse).thumbs_up !== null) {
          const responseData = q.data as LLMFeedbackResponse;
          const responseId = responseData.response_item_id;

          // Only update if this feedback doesn't exist yet or has changed
          setFeedback((prev) => {
            const existingFeedback = prev[responseId];

            // Skip update if feedback already exists and hasn't changed
            if (
              existingFeedback &&
              existingFeedback.thumbs_up === responseData.thumbs_up &&
              existingFeedback.feedback_text === responseData.feedback_text
            ) {
              return prev;
            }

            return {
              ...prev,
              [responseId]: responseData,
            };
          });
        }
      });
    }
  }, [feedbackQueries]);

  const handleOpenFeedback =
    (thumbsUp: boolean, response_item_id: string) => () => {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.message.type,
        subtype: thumbsUp
          ? CHATBV_GA4_EVENTS.message.subTypes.thumbs_up_click
          : CHATBV_GA4_EVENTS.message.subTypes.thumbs_down_click,
      });
      setThumbsUp(thumbsUp);
      setCurrentResponseId(response_item_id);
      setOpenFeedback(true);
    };

  const handleCloseFeedback = () => {
    setCurrentResponseId('');
    setOpenFeedback(false);
  };

  return {
    openFeedback,
    thumbsUp,
    currentResponseId,
    feedback,
    createFeedback,
    updateFeedback,
    handleOpenFeedback,
    handleCloseFeedback,
  };
};
